import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Inbox, Loader2, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import { ErrorState } from '@/components/pos/ErrorState';
import { useMyApprovalRequests } from '@/hooks/queries';
import { useWithdrawApprovalRequest } from '@/hooks/mutations';
import {
  STATUS_CHIP_CLASS,
  approvalAmount,
  approvalErrorMessage,
  approvalReason,
  approverName,
  kindLabel,
  statusLabel,
} from '@/lib/approvals';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { SaleApprovalRequest } from '@/types/sale';

const PER_PAGE = 20;
const POLL_MS = 10_000;

const FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Semua' },
  { value: 'PENDING', label: 'Menunggu' },
  { value: 'CONSUMED', label: 'Selesai' },
  { value: 'APPROVED', label: 'Disetujui' },
  { value: 'REJECTED', label: 'Ditolak' },
  { value: 'EXPIRED', label: 'Kedaluwarsa' },
  { value: 'WITHDRAWN', label: 'Ditarik' },
];

const CHIP_BASE = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium';

/**
 * "Permintaan Void/Refund" — the operator's own requests and where each one
 * stands. Polls every 10 s so a supervisor's decision in the BO shows up at
 * the counter; a PENDING request can be withdrawn.
 */
export function RequestsPage() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [withdrawTarget, setWithdrawTarget] = useState<SaleApprovalRequest | null>(null);

  useEffect(() => setPage(1), [status]);

  const { data, isLoading, isFetching, isError, error, refetch } = useMyApprovalRequests(
    { status: status || undefined, page, perPage: PER_PAGE },
    { refetchInterval: POLL_MS },
  );
  const rows = data?.rows ?? [];
  const totalPage = data?.totalPage ?? 1;
  const withdrawMut = useWithdrawApprovalRequest();

  const confirmWithdraw = () => {
    if (!withdrawTarget) return;
    withdrawMut.mutate(withdrawTarget.id, {
      onSuccess: () => {
        toast.success('Permintaan ditarik.');
        setWithdrawTarget(null);
      },
      onError: (err) => {
        toast.error(approvalErrorMessage(err));
        setWithdrawTarget(null);
      },
    });
  };

  return (
    <div className="mx-auto flex h-full max-w-4xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">
            Permintaan Void/Refund
          </h1>
          <p className="text-sm text-muted-foreground">
            Pengajuan Anda — disetujui supervisor di back office.
          </p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Segarkan
        </Button>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList className="flex-wrap">
          {FILTERS.map((f) => (
            <TabsTrigger key={f.value || 'all'} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <ScrollArea className="min-h-0 flex-1">
        {isLoading ? (
          <div className="flex justify-center py-16 text-ink-400">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
        ) : isError && rows.length === 0 ? (
          <ErrorState message={approvalErrorMessage(error)} onRetry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Belum ada permintaan"
            description="Ajukan void atau refund dari halaman Pesanan."
          />
        ) : (
          <ul className="space-y-2 pr-2">
            {rows.map((r) => (
              <RequestRow key={r.id} row={r} onWithdraw={() => setWithdrawTarget(r)} />
            ))}
          </ul>
        )}
      </ScrollArea>

      {totalPage > 1 && (
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" /> Sebelumnya
          </Button>
          <span className="text-xs text-ink-400">
            Halaman {page} dari {totalPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={page >= totalPage || isFetching}
            onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
          >
            Berikutnya <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <AlertDialog open={withdrawTarget !== null} onOpenChange={(o) => !o && setWithdrawTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tarik permintaan?</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawTarget
                ? `${kindLabel(withdrawTarget.kind)} untuk ${withdrawTarget.sale?.invoiceId ?? 'transaksi ini'} tidak akan diproses supervisor.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={withdrawMut.isPending}>Batal</AlertDialogCancel>
            <AlertDialogAction
              disabled={withdrawMut.isPending}
              onClick={(e) => {
                e.preventDefault();
                confirmWithdraw();
              }}
            >
              {withdrawMut.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Tarik permintaan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RequestRow({ row, onWithdraw }: { row: SaleApprovalRequest; onWithdraw: () => void }) {
  const status = String(row.status);
  const approver = approverName(row);
  const reason = approvalReason(row);
  return (
    <li>
      <Card>
        <CardContent className="flex flex-col gap-2 py-3 pt-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="tnum font-display font-semibold text-ink-900">
                {row.sale?.invoiceId ?? '—'}
              </span>
              <span className={cn(CHIP_BASE, 'bg-secondary text-ink-900')}>{kindLabel(row.kind)}</span>
              <span
                className={cn(CHIP_BASE, STATUS_CHIP_CLASS[status] ?? 'bg-slate-100 text-slate-600')}
                data-testid="approval-status"
              >
                {statusLabel(status)}
              </span>
            </div>
            {reason && <p className="break-words text-sm text-ink-600">{reason}</p>}
            <p className="text-xs text-ink-400">
              Diajukan {formatDateTime(row.createdAt)}
              {status === 'PENDING' && row.expiresAt ? ` · berlaku s.d. ${formatDateTime(row.expiresAt)}` : ''}
              {approver && ['APPROVED', 'CONSUMED'].includes(status) ? ` · disetujui ${approver}` : ''}
              {row.executedAt ? ` · diproses ${formatDateTime(row.executedAt)}` : ''}
            </p>
            {status === 'REJECTED' && row.rejectionReason && (
              <p className="text-xs font-medium text-destructive">Ditolak: {row.rejectionReason}</p>
            )}
            {row.executionError && (
              <p className="text-xs font-medium text-destructive">
                Gagal diproses: {row.executionError}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-row items-center gap-2 sm:flex-col sm:items-end">
            <MoneyValue value={approvalAmount(row)} className="text-sm font-semibold" />
            {status === 'PENDING' && (
              <Button variant="outline" size="sm" className="gap-1" onClick={onWithdraw}>
                <Undo2 className="h-3.5 w-3.5" /> Tarik
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </li>
  );
}
